import { useState, useEffect, useMemo } from "react"
import { FiUser, FiUsers, FiClock } from 'react-icons/fi'
import { BiSolidImageAdd } from 'react-icons/bi'
import { ReactComponent as GeneratorIcon } from '../../../shared/img/icon/generator.svg'
import api from '../../../utils/api'
import Input from "../../../shared/components/form/input"
import Textarea from "../../../shared/components/form/textarea"
import Button from "../../../shared/components/Button/Button"
import FormLayout from "../../../shared/components/FormLayout/FormLayout"
import FormSection from "../../../shared/components/FormLayout/FormSection"
import PhotoGallery from "../../../shared/components/PhotoGallery/PhotoGallery"
import AuthImage from "../../../shared/components/AuthImage/AuthImage"
import AddPhotoModal from "../../../shared/components/AddPhotoModal/AddPhotoModal"
import ConfirmDeleteModal from "../../../shared/components/ConfirmDeleteModal/ConfirmDeleteModal"
import useFlashMessage from '../../../hooks/useFlashMessage'
import './ReviewServiceOrder.css'
import { buildChecklistSections } from '../../../utils/checklistSections'

const InfoField = ({ icon: Icon, label, value }) => (
    <div className="sor-info-field">
        <span className="sor-info-label">
            {Icon && <Icon size={11} />}
            {label}
        </span>
        <span className="sor-info-value">{value || '—'}</span>
    </div>
)

function formatDuration(totalSeconds) {
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    if (h === 0) return `${m}min`
    return `${h}h ${m}min`
}

function ReviewServiceOrder({ id, btCancelAction, handleOnSuccess }) {

    const [token] = useState(localStorage.getItem('token') || '')
    const [serviceOrder, setServiceOrder] = useState(null)
    const [confirmClose, setConfirmClose] = useState(false)
    const [photoToRemove, setPhotoToRemove] = useState(null)
    const [modalAddPhoto, setModalAddPhoto] = useState(false)
    const { setFlashMessage } = useFlashMessage()

    useEffect(() => {
        api.get(`/serviceOrder/get/${id}`, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
            .then((response) => setServiceOrder(response.data.serviceOrder))
    }, [id, token])

    const checklistSections = useMemo(
        () => buildChecklistSections(serviceOrder?.checklist || []),
        [serviceOrder?.checklist]
    )

    function handleResponseChange(itemId, value) {
        setServiceOrder(prev => ({
            ...prev,
            checklist: (prev.checklist || []).map(item => item._id === itemId ? { ...item, response: value } : item),
        }))
    }

    function handleObservationChange(e) {
        setServiceOrder(prev => ({ ...prev, observation: e.target.value }))
    }

    function handleInternalObservationChange(e) {
        setServiceOrder(prev => ({ ...prev, internalObservation: e.target.value }))
    }

    function confirmRemoveAttachment() {
        const photo = photoToRemove
        setServiceOrder(prev => ({
            ...prev,
            attachments: (prev.attachments || []).filter(a => a._id !== photo._id),
        }))
        setPhotoToRemove(null)
    }

    async function savePhoto(file, description) {
        if (!file) {
            setFlashMessage('Selecione uma foto antes de adicionar.', 'error')
            return
        }

        const formData = new FormData()
        formData.append('photo', file)
        formData.append('description', description)

        let msgType = 'success'
        const data = await api.post(`/serviceOrder/photo/${id}`, formData, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data)
            .catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            setServiceOrder((prev) => ({ ...prev, attachments: data.serviceOrder.attachments }))
            setModalAddPhoto(false)
        }
    }

    async function saveServiceOrder() {
        let msgType = 'success'
        const data = await api.patch(`/serviceOrder/update/${id}`, serviceOrder, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data)
            .catch((err) => { msgType = 'error'; return err.response.data })

        return { ok: msgType === 'success', message: data.message }
    }

    async function handleSave(e) {
        e?.preventDefault()
        const result = await saveServiceOrder()
        setFlashMessage(result.message, result.ok ? 'success' : 'error')
    }

    async function handleClose() {
        const saveResult = await saveServiceOrder()
        if (!saveResult.ok) {
            setFlashMessage(saveResult.message, 'error')
            return
        }

        let msgType = 'success'
        const data = await api.patch(`/serviceOrder/close/${id}`, {}, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data)
            .catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            handleOnSuccess()
        }
    }

    if (!serviceOrder) {
        return <div className="pg-loading">Carregando ordem de serviço...</div>
    }

    return (
        <>
        <FormLayout
            title={`Revisar OS ${serviceOrder.code || ''}`}
            onSubmit={handleSave}
            actions={
                confirmClose ? (
                    <>
                        <Button variant="finish" type="button" onClick={handleClose}>
                            Sim, concluir
                        </Button>
                        <Button variant="cancel" type="button" onClick={() => setConfirmClose(false)}>
                            Cancelar
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant="save" type="submit">
                            Salvar
                        </Button>
                        <Button variant="finish" type="button" onClick={() => setConfirmClose(true)}>
                            Concluir
                        </Button>
                        {btCancelAction && (
                            <Button variant="cancel" type="button" onClick={btCancelAction}>
                                Fechar
                            </Button>
                        )}
                    </>
                )
            }
        >
            {confirmClose && (
                <p className="sor-confirm-close-notice">
                    Ao concluir, a OS sai de "Em análise" e vai para "Concluída". Confirma o encerramento?
                </p>
            )}

            <FormSection label="Informações da OS">
                <div className="sor-info-grid">
                    <InfoField icon={FiUser} label="Cliente" value={serviceOrder.client?.label || serviceOrder.client?.name} />
                    <div className="sor-info-field">
                        <span className="sor-info-label">
                            <GeneratorIcon className="sor-info-label-icon" />
                            Gerador
                        </span>
                        <span className="sor-info-value">{serviceOrder.generator?.label || '—'}</span>
                    </div>
                    <InfoField
                        icon={FiUsers}
                        label="Técnico(s)"
                        value={(serviceOrder.technicians || []).map(t => t.name || t.label).join(', ')}
                    />
                    {typeof serviceOrder.timeSpentSeconds === 'number' && (
                        <InfoField icon={FiClock} label="Tempo gasto" value={formatDuration(serviceOrder.timeSpentSeconds)} />
                    )}
                </div>
            </FormSection>

            <FormSection label="Informações do técnico">
                <div className="form-group">
                    <Textarea
                        name="observation"
                        placeholder="Nenhuma observação registrada"
                        handleOnChange={handleObservationChange}
                        value={serviceOrder.observation || ''}
                    />
                </div>
            </FormSection>

            <FormSection label="Observação interna">
                {/* Uso entre técnicos — nunca entra no relatório/PDF entregue ao
                    cliente (ver ServiceOrderPDFPreview.js, que não lê este campo). */}
                <div className="form-group">
                    <Textarea
                        name="internalObservation"
                        placeholder="Nenhuma observação interna registrada"
                        handleOnChange={handleInternalObservationChange}
                        value={serviceOrder.internalObservation || ''}
                    />
                </div>
            </FormSection>

            <FormSection
                label="Anexos"
                actions={
                    <button
                        type="button"
                        className="sor-attachment-add-btn"
                        title="Adicionar anexo"
                        onClick={() => setModalAddPhoto(true)}
                    >
                        <BiSolidImageAdd size={20} />
                    </button>
                }
            >
                <PhotoGallery
                    photos={serviceOrder.attachments}
                    imageBasePath="/images/serviceOrder"
                    onRemove={setPhotoToRemove}
                    emptyText="Nenhuma foto enviada pelo técnico."
                />
            </FormSection>

            <FormSection label="Checklist e preenchimento">
                {checklistSections.length > 0 ? (
                    checklistSections.map((section) => (
                        <div key={section.groupLabel} className="sor-checklist-group">
                            <div className="sor-checklist-group-header">{section.groupLabel}</div>
                            {section.items.map((item) => (
                                <div key={item._id} className="sor-checklist-item">
                                    <span className="sor-checklist-item-name">{item.name}</span>
                                    {item.fillType?.value === 'yesno' ? (
                                        <div className="sor-yesno-group">
                                            <button
                                                type="button"
                                                className={`sor-yesno-btn ${item.response === 'Sim' ? 'sor-yesno-btn-active' : ''}`}
                                                onClick={() => handleResponseChange(item._id, 'Sim')}
                                            >
                                                Sim
                                            </button>
                                            <button
                                                type="button"
                                                className={`sor-yesno-btn ${item.response === 'Não' ? 'sor-yesno-btn-active' : ''}`}
                                                onClick={() => handleResponseChange(item._id, 'Não')}
                                            >
                                                Não
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="sor-checklist-item-input">
                                            <Input
                                                type="text"
                                                name={`response_${item._id}`}
                                                value={item.response || ''}
                                                handleOnChange={(e) => handleResponseChange(item._id, e.target.value)}
                                            />
                                            {item.fillType?.value === 'measurement' && item.unit && (
                                                <span className="sor-checklist-item-unit">{item.unit}</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))
                ) : (
                    <p className="sor-checklist-empty">Nenhum item de checklist nesta OS</p>
                )}
            </FormSection>

            {serviceOrder.signature && (
                <FormSection label="Assinatura do cliente">
                    <AuthImage
                        className="sor-signature-img"
                        src={`/images/serviceOrder/${serviceOrder.signature}`}
                        alt="Assinatura do cliente"
                    />
                </FormSection>
            )}

            <div className="sor-bottom-spacer" />

        </FormLayout>

        <AddPhotoModal
            modalStatus={modalAddPhoto}
            changeStatus={setModalAddPhoto}
            onSave={savePhoto}
            title="Adicionar anexo"
            imageBasePath="/images/serviceOrder"
        />

        <ConfirmDeleteModal
            modalStatus={!!photoToRemove}
            changeStatus={() => setPhotoToRemove(null)}
            onConfirm={confirmRemoveAttachment}
            title="Remover anexo"
            message="Deseja realmente remover esta foto da OS?"
            itemName={photoToRemove?.description}
            confirmText="Remover"
        />
        </>
    )
}

export default ReviewServiceOrder
